import React, { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Typography, Button, Box, Dialog, DialogActions, DialogContent,
    DialogContentText, DialogTitle
} from '@mui/material';
import {
    TextInput,
    required,
} from 'react-admin';

// (Import your custom PlateNumber component, or define it inline)
import { CarPlateInput } from './CarePlate'; 

/**
 * EditableRecordFields:
 * Renders an editable table for top-level record fields.
 */
const EditableRecordFields = ({ title, columns }) => {
    const { control, setValue, getValues } = useFormContext();

    // Watch the entire record (or only the fields in columns)
    const watchedRecord = useWatch({ control }) || {};

    // Local state to store the editing session
    const [localData, setLocalData] = useState(() => getValues());
    const [editSnapshot, setEditSnapshot] = useState(() => getValues());
    const [isEditable, setIsEditable] = useState(false);
    const [isModified, setIsModified] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);

    // Sync external data only if we're not currently editing
    useEffect(() => {
        const recordString = JSON.stringify(watchedRecord);
        const localString = JSON.stringify(localData);

        if (!isEditable && recordString !== localString) {
            setLocalData(watchedRecord);
            setEditSnapshot(watchedRecord);
        }
    }, [watchedRecord, isEditable]);

    // Handle change in any field
    const handleFieldChange = (field, value) => {
        setLocalData((prev) => ({ ...prev, [field]: value }));
        setIsModified(true);
    };

    // Save all changes back to the react-hook-form state
    const handleSave = () => {
        columns.forEach((column) => {
            const fieldSource = column.source;
            setValue(fieldSource, localData[fieldSource]);
        });
        setEditSnapshot(localData);
        setIsEditable(false);
        setIsModified(false);
    };

    // Undo changes to the snapshot
    const handleUndo = () => {
        setLocalData(editSnapshot);
        setIsModified(false);
    };

    // Dialog control
    const handleOpenDialog = () => setOpenDialog(true);
    const handleCloseDialog = () => setOpenDialog(false);
    const confirmEdit = () => {
        setIsEditable(true);
        handleCloseDialog();
    };

    /**
     * Renders the correct input based on `column.type`.
     * For read-only fields, we simply show a TextInput that is disabled.
     * For the 'plate_number' type, we use a CarPlateInput as an example.
     */
    const renderInput = (column) => {
        const { source, label, type ,disabled} = column;
        const fieldValue = localData[source] || '';
        const isInputDisabled = !isEditable || disabled;

        switch (type) {
            case 'plate_number':
                // Example of a custom plate input. 
                // Make sure CarPlateInput onChange returns the new value.
                return (
                    <CarPlateInput
                        source={source}
                        label={label}
                        value={fieldValue}
                        disabled={isInputDisabled}
                        onChange={(e) => handleFieldChange(source, e.target.value)}
                    />
                );

            case 'readonly':
                // For read-only columns, use a disabled TextInput (or just display the value).
                return (
                    <TextInput
                        source={source}
                        label={label}
                        value={fieldValue}
                        disabled
                        variant="standard"
                        fullWidth
                    />
                );

            // If you have other specific types like 'date_input', 'number', etc.
            // Add additional case statements here.

            default:
                // Default to a standard TextInput, respecting isInputDisabled
                return (
                    <TextInput
                        source={source}
                        label={label}
                        value={fieldValue}
                        variant="standard"
                        fullWidth
                        disabled={isInputDisabled}
                        onChange={(e) => handleFieldChange(source, e.target.value)}
                    />
                );
        }
    };

    return (
        <Box>
            <TableContainer component={Paper} style={{ maxHeight: '400px', overflow: 'auto' }}>
                <Typography variant="h6" style={{ margin: '1em' }}>
                    {title}
                </Typography>
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell key={column.source}>
                                    {column.label}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        <TableRow>
                            {columns.map((column) => (
                                <TableCell key={column.source}>
                                    {renderInput(column)}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableBody>
                </Table>
            </TableContainer>

            <Box display="flex" justifyContent="space-between" marginTop="1em">
                {isEditable ? (
                    <>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSave}
                            disabled={!isModified}
                        >
                            حفظ {title}
                        </Button>
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleUndo}
                            disabled={!isModified}
                        >
                            تراجع
                        </Button>
                    </>
                ) : (
                    <Button variant="contained" color="primary" onClick={handleOpenDialog}>
                        تعديل {title}
                    </Button>
                )}
            </Box>

            {/* Confirmation dialog for toggling edit mode */}
            <Dialog open={openDialog} onClose={handleCloseDialog}>
                <DialogTitle>تأكيد التعديل</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        سيتم تعيين البيانات الى <strong>“تم تعديل البيانات يدوياً”</strong> 
                        عند التعديل. هل تريد المتابعة؟
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog} color="secondary">
                        لا
                    </Button>
                    <Button onClick={confirmEdit} color="primary" autoFocus>
                        نعم
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default EditableRecordFields;
